import React from "react";
import EmployeeProfileTabHeader from "./EmployeeProfileTabHeader";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import EmployeeProfileEmptyState from "./EmployeeProfileEmptyState";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuItem from "@mui/material/MenuItem";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import Select from "@mui/material/Select";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../../shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../../shared/helpers/ButtonOutlined";
import EmployeeDeleteWarningModal from "./EmployeeDeleteWarningModal";
import SecurityIcon from "@mui/icons-material/Security";
import StoreIcon from "@mui/icons-material/Store";
import NotesIcon from "@mui/icons-material/Notes";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import BlockIcon from "@mui/icons-material/Block";
import DescriptionIcon from "@mui/icons-material/Description";
import HandymanIcon from "@mui/icons-material/Handyman";
import ComputerIcon from "@mui/icons-material/Computer";
import CampaignIcon from "@mui/icons-material/Campaign";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";

const SPECIAL_ACCESS_NONE_VALUE = "__special_access_none__";

const createDefaultModalData = () => ({
  email: "",
  first_name: "",
  id: null,
  last_name: "",
  permission_type: "",
  phone_number: null,
  store: null,
  permission_type_overrides: [],
});

const SPECIAL_ACCESS_LOOKUP = {
  logbook_access: "Logbook",
  manager_notifications: "Manager Notifications",
  publish_schedule_approver: "Schedule Publisher",
  documents_editor: "Documents Editor",
  maintenance_access: "Maintenance",
  it_access: "IT",
  marketing_access: "Marketing",
  menu_access: "Menu",
};

const SPECIAL_ACCESS_TOOLTIPS = {
  logbook_access: {
    company_employee: "Can view and create logbook entries",
  },
  manager_notifications: {
    company_admin: "Receives manager-level push notifications",
  },
  publish_schedule_approver: {
    company_admin: "Can publish schedules for their stores",
    company_manager: "Can publish schedules for their stores",
  },
  documents_editor: {
    company_admin: "Can upload and manage shared documents",
    company_manager: "Can upload and manage shared documents",
  },
  maintenance_access: {
    company_admin: "Can be assigned to maintenance tickets and is added to the maintenance group chat",
    company_manager: "Can be assigned to maintenance tickets",
    company_employee: "Can view and manage maintenance tickets, be assigned to them, and is added to the maintenance group chat",
  },
  it_access: {
    company_admin: "Can be assigned to IT tickets and is added to the IT group chat",
    company_manager: "Can be assigned to IT tickets",
    company_employee: "Can view and manage IT tickets, be assigned to them, and is added to the IT group chat",
  },
  marketing_access: {
    company_admin: "Can be assigned to marketing tickets and is added to the marketing group chat",
    company_manager: "Can be assigned to marketing tickets",
    company_employee: "Can view and manage marketing tickets, be assigned to them, and is added to the marketing group chat",
  },
  menu_access: {
    company_admin: "Can be assigned to menu tickets and is added to the menu group chat",
    company_manager: "Can be assigned to menu tickets",
    company_employee: "Can view and manage menu tickets, be assigned to them, and is added to the menu group chat",
  },
};

function EmployeeManageAccessTab(props) {
  const [selectedStmId, setSelectedStmId] = React.useState(null);
  const [selectedStoreId, setSelectedStoreId] = React.useState(null);
  const [selectedStoreName, setSelectedStoreName] = React.useState(null);

  const [open, setOpen] = React.useState(false);
  const [modalData, setModalData] = React.useState(createDefaultModalData);
  const [modalEdit, setModalEdit] = React.useState(false);
  const [openEmployeeDeleteWarningModal, setOpenEmployeeDeleteWarningModal] =
    React.useState(false);
  const [specialAccessTouched, setSpecialAccessTouched] = React.useState(false);
  const [applyToAll, setApplyToAll] = React.useState(false);

  const resetModalState = () => {
    setModalData(createDefaultModalData());
    setSpecialAccessTouched(false);
    setApplyToAll(false);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    resetModalState();
    setOpen(false);
  };

  const handleOpenEmployeeDeleteWarningModal = () => {
    setOpenEmployeeDeleteWarningModal(true);
  };

  const handleCloseEmployeeDeleteWarningModal = () => {
    setOpenEmployeeDeleteWarningModal(false);
  };

  // New handle functions for Select components
  const handleStoreChange = (event) => {
    const selectedStore = props.storeList.find(
      (store) => store.id === event.target.value
    );
    setModalData((prevData) => ({
      ...prevData,
      store: selectedStore || null,
    }));
  };

  const handlePermissionChange = (event) => {
    const newPermissionType = event.target.value;

    // Define which overrides are valid for each permission type
    const getValidOverridesForPermissionType = (permType) => {
      switch (permType) {
        case "company_admin":
          return ["manager_notifications", "publish_schedule_approver", "documents_editor", "maintenance_access", "it_access", "marketing_access", "menu_access"];
        case "company_manager":
          return ["publish_schedule_approver", "documents_editor", "maintenance_access", "it_access", "marketing_access", "menu_access"];
        case "company_employee":
          return ["logbook_access", "maintenance_access", "it_access", "marketing_access", "menu_access"];
        default:
          return [];
      }
    };

    const validOverrides = getValidOverridesForPermissionType(newPermissionType);

    setModalData((prevData) => ({
      ...prevData,
      permission_type: newPermissionType,
      // Filter out any overrides that are no longer valid for the new permission type
      permission_type_overrides: (prevData.permission_type_overrides || []).filter(
        (override) => validOverrides.includes(override)
      ),
    }));
  };

  const handlePermissionOverrideChange = (event) => {
    const value = event.target.value;

    setSpecialAccessTouched(true);
    // MUI multiselect returns an array
    // Filter out any empty strings or the "none" value
    const filteredValue = Array.isArray(value)
      ? value.filter((v) => v && v !== SPECIAL_ACCESS_NONE_VALUE)
      : [];

    setModalData((prevData) => ({
      ...prevData,
      permission_type_overrides: filteredValue,
    }));
  };

  // Validation function
  const checkIfValid = () => {
    return modalData.store?.id && modalData.permission_type;
  };

  const getPermissionInfo = (permissionType) => {
    if (permissionType === "company_admin") {
      return {
        label: "Admin",
        style: "border border-violet-600 bg-violet-50 text-violet-600",
      };
    } else if (permissionType === "company_manager") {
      return {
        label: "Manager",
        style: "border border-orange-600 bg-orange-50 text-orange-600",
      };
    } else if (permissionType === "company_timeclock") {
      return {
        label: "Time Clock",
        style: "border border-amber-600 bg-amber-50 text-amber-700",
      };
    } else {
      return {
        label: "Employee",
        style: "border border-blue-600 bg-blue-50 text-blue-600",
      };
    }
  };

  const getSpecialAccessIcon = (specialAccess) => {
    switch (specialAccess) {
      case 'logbook_access':
        return NotesIcon;
      case 'manager_notifications':
        return NotificationsIcon;
      case 'publish_schedule_approver':
        return TaskAltIcon;
      case 'documents_editor':
        return DescriptionIcon;
      case 'maintenance_access':
        return HandymanIcon;
      case 'it_access':
        return ComputerIcon;
      case 'marketing_access':
        return CampaignIcon;
      case 'menu_access':
        return RestaurantMenuIcon;
      default:
        return null;
    }
  };

  const getSpecialAccessStyles = (specialAccess) => {
    switch (specialAccess) {
      case 'manager_notifications':
        return { bg: 'bg-emerald-100', text: 'text-emerald-600' };
      case 'publish_schedule_approver':
        return { bg: 'bg-violet-100', text: 'text-violet-600' };
      case 'documents_editor':
        return { bg: 'bg-sky-100', text: 'text-sky-600' };
      case 'maintenance_access':
        return { bg: 'bg-amber-100', text: 'text-amber-600' };
      case 'it_access':
        return { bg: 'bg-blue-100', text: 'text-blue-600' };
      case 'marketing_access':
        return { bg: 'bg-pink-100', text: 'text-pink-600' };
      case 'menu_access':
        return { bg: 'bg-orange-100', text: 'text-orange-600' };
      default:
        return { bg: 'bg-purple-100', text: 'text-purple-600' };
    }
  };

  // Only admins can manage special access for admins/managers
  const currentUserIsAdmin =
    props.permissionRole === "company_admin" ||
    props.permissionRole === "rightwork_internal";

  // Get array of store IDs where employee is already a member
  const existingStoreIds = React.useMemo(() => {
    return (
      props.relatedTeamMembersData?.map((member) => member.store?.id) || []
    );
  }, [props.relatedTeamMembersData]);

  // Get store IDs where the current manager is a member
  const managerStoreIds = React.useMemo(() => {
    return props.storeNames?.map((s) => s.id) || [];
  }, [props.storeNames]);

  // Filter store list to exclude stores where employee is already a member
  // Company admins can see all stores; non-admins can only see stores they belong to
  const availableStores = React.useMemo(() => {
    return (
      props.storeList?.filter(
        (store) =>
          !existingStoreIds.includes(store.id) &&
          (props.isCompanyAdmin || managerStoreIds.includes(store.id))
      ) || []
    );
  }, [props.storeList, existingStoreIds, props.isCompanyAdmin, managerStoreIds]);

  // When editing, include the current store in the available stores
  const storeOptions = React.useMemo(() => {
    if (modalEdit && modalData?.store) {
      return [...availableStores, modalData.store];
    }
    return availableStores;
  }, [availableStores, modalEdit, modalData?.store]);

  const selectedStore = React.useMemo(() => {
    if (!modalData?.store?.id) return null;
    return (
      props.storeList?.find((store) => store.id === modalData.store.id) ||
      modalData.store
    );
  }, [modalData?.store, props.storeList]);

  const schedulePublisherEnabled = Boolean(
    selectedStore?.publish_schedule_review_required_enabled ||
      modalData?.store?.publish_schedule_review_required_enabled
  );
  const schedulePublisherSelected =
    modalData?.permission_type_overrides?.includes("publish_schedule_approver");
  const showSchedulePublisher =
    schedulePublisherEnabled || schedulePublisherSelected;

  const shouldShowSpecialAccessSelect =
    // Admins/managers can only have special access managed by admins
    ((modalData.permission_type === "company_admin" ||
      modalData.permission_type === "company_manager") && currentUserIsAdmin) ||
    // Employees can have special access managed by managers (if any feature is enabled)
    (modalData.permission_type === "company_employee" && (props.logbookEnabled || props.maintenanceEnabled || props.itTicketsEnabled || props.marketingTicketsEnabled || props.menuTicketsEnabled));

  // For multiselect, use the array directly (default to empty array)
  const specialAccessValue = modalData?.permission_type_overrides || [];

  const specialAccessOptions = React.useMemo(() => {
    if (!shouldShowSpecialAccessSelect) {
      return [];
    }

    const options = [];

    if (modalData.permission_type === "company_admin") {
      // Only admins can see this dropdown for admin roles (per shouldShowSpecialAccessSelect)
      options.push({
        value: "manager_notifications",
        label: SPECIAL_ACCESS_LOOKUP.manager_notifications,
        disabled: false,
      });

      if (showSchedulePublisher) {
        options.push({
          value: "publish_schedule_approver",
          label: SPECIAL_ACCESS_LOOKUP.publish_schedule_approver,
          disabled: !schedulePublisherEnabled,
        });
      }

      options.push({
        value: "documents_editor",
        label: SPECIAL_ACCESS_LOOKUP.documents_editor,
        disabled: false,
      });

      if (props.maintenanceEnabled) {
        options.push({
          value: "maintenance_access",
          label: SPECIAL_ACCESS_LOOKUP.maintenance_access,
          disabled: false,
        });
      }
      if (props.itTicketsEnabled) {
        options.push({
          value: "it_access",
          label: SPECIAL_ACCESS_LOOKUP.it_access,
          disabled: false,
        });
      }
      if (props.marketingTicketsEnabled) {
        options.push({
          value: "marketing_access",
          label: SPECIAL_ACCESS_LOOKUP.marketing_access,
          disabled: false,
        });
      }
      if (props.menuTicketsEnabled) {
        options.push({
          value: "menu_access",
          label: SPECIAL_ACCESS_LOOKUP.menu_access,
          disabled: false,
        });
      }
    } else if (modalData.permission_type === "company_manager") {
      // Only admins can see this dropdown for manager roles (per shouldShowSpecialAccessSelect)
      if (showSchedulePublisher) {
        options.push({
          value: "publish_schedule_approver",
          label: SPECIAL_ACCESS_LOOKUP.publish_schedule_approver,
          disabled: !schedulePublisherEnabled,
        });
      }

      options.push({
        value: "documents_editor",
        label: SPECIAL_ACCESS_LOOKUP.documents_editor,
        disabled: false,
      });

      if (props.maintenanceEnabled) {
        options.push({
          value: "maintenance_access",
          label: SPECIAL_ACCESS_LOOKUP.maintenance_access,
          disabled: false,
        });
      }
      if (props.itTicketsEnabled) {
        options.push({
          value: "it_access",
          label: SPECIAL_ACCESS_LOOKUP.it_access,
          disabled: false,
        });
      }
      if (props.marketingTicketsEnabled) {
        options.push({
          value: "marketing_access",
          label: SPECIAL_ACCESS_LOOKUP.marketing_access,
          disabled: false,
        });
      }
      if (props.menuTicketsEnabled) {
        options.push({
          value: "menu_access",
          label: SPECIAL_ACCESS_LOOKUP.menu_access,
          disabled: false,
        });
      }
    } else if (modalData.permission_type === "company_employee") {
      // Managers and admins can see this dropdown for employee roles
      if (props.logbookEnabled) {
        options.push({
          value: "logbook_access",
          label: SPECIAL_ACCESS_LOOKUP.logbook_access,
          disabled: false,
        });
      }
      if (props.maintenanceEnabled) {
        options.push({
          value: "maintenance_access",
          label: SPECIAL_ACCESS_LOOKUP.maintenance_access,
          disabled: false,
        });
      }
      if (props.itTicketsEnabled) {
        options.push({
          value: "it_access",
          label: SPECIAL_ACCESS_LOOKUP.it_access,
          disabled: false,
        });
      }
      if (props.marketingTicketsEnabled) {
        options.push({
          value: "marketing_access",
          label: SPECIAL_ACCESS_LOOKUP.marketing_access,
          disabled: false,
        });
      }
      if (props.menuTicketsEnabled) {
        options.push({
          value: "menu_access",
          label: SPECIAL_ACCESS_LOOKUP.menu_access,
          disabled: false,
        });
      }
    }

    return options;
  }, [
    modalData.permission_type,
    props.logbookEnabled,
    props.maintenanceEnabled,
    props.itTicketsEnabled,
    props.marketingTicketsEnabled,
    props.menuTicketsEnabled,
    schedulePublisherEnabled,
    shouldShowSpecialAccessSelect,
    showSchedulePublisher,
  ]);

  const renderSpecialAccessValue = (selected) => {
    if (!selected || selected.length === 0) {
      return specialAccessTouched ? "None" : (
        <span className="text-slate-400">Select Special Access</span>
      );
    }

    // For multiselect, show comma-separated labels
    return selected
      .map((value) => SPECIAL_ACCESS_LOOKUP[value] || value)
      .join(", ");
  };

  const hasAccess = props.relatedTeamMembersData?.length > 0;

  return (
    <>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <EmployeeProfileTabHeader
          icon={SecurityIcon}
          title="Manage Access"
          subtitle="Control store access and permissions"
          actionLabel="Add Store Access"
          actionIcon={AddIcon}
          onAction={() => {
            resetModalState();
            setModalEdit(false);
            handleOpen();
          }}
        />

        {/* Access Container */}
        {hasAccess ? (
          <EmployeeProfileSectionCard
            bodyClassName="p-0"
            header={
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Store</span>
                </div>
                <div className="col-span-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Permission</span>
                </div>
                <div className="col-span-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Special Access</span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</span>
                </div>
              </div>
            }
          >
            {/* Access List */}
            <div className="divide-y divide-gray-100">
              {props.relatedTeamMembersData?.map((member, index) => {
                const permissionInfo = getPermissionInfo(member?.permission_type);
                const isRestricted = member?.permission_type === "company_admin" &&
                  (props.permissionRole !== "company_admin" && props.permissionRole !== "rightwork_internal");

                return (
                  <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Store */}
                      <div className="col-span-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                            <StoreIcon className="text-gray-600" style={{ fontSize: "16px" }} />
                          </div>
                          <span className="font-medium text-gray-900">{member?.store?.store_name}</span>
                        </div>
                      </div>

                      {/* Permission */}
                      <div className="col-span-3">
                        <span className={`px-3 py-1.5 rounded-lg text-sm ${permissionInfo.style}`}>
                          {permissionInfo.label}
                        </span>
                      </div>

                      {/* Special Access */}
                      <div className="col-span-3">
                        {member?.permission_type_overrides?.length > 0 ? (
                          <div className="grid grid-cols-2 gap-y-1.5" style={{ gridTemplateColumns: "120px 120px" }}>
                            {member.permission_type_overrides.map((accessKey) => {
                              const Icon = getSpecialAccessIcon(accessKey);
                              const styles = getSpecialAccessStyles(accessKey);
                              const tooltipText = SPECIAL_ACCESS_TOOLTIPS[accessKey]?.[member.permission_type];
                              const badge = (
                                <div key={accessKey} className="flex items-center space-x-1 w-fit">
                                  {Icon && (
                                    <div
                                      className={`w-6 h-6 rounded flex items-center justify-center ${styles.bg}`}
                                    >
                                      <Icon
                                        className={styles.text}
                                        style={{ fontSize: "14px" }}
                                      />
                                    </div>
                                  )}
                                  <span className="text-gray-900 text-sm">
                                    {SPECIAL_ACCESS_LOOKUP[accessKey]}
                                  </span>
                                </div>
                              );
                              return tooltipText ? (
                                <Tooltip key={accessKey} title={tooltipText} arrow placement="top">
                                  {badge}
                                </Tooltip>
                              ) : badge;
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">None</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="col-span-2">
                        {isRestricted ? (
                          <div className="flex items-center justify-end space-x-2">
                            <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-2 py-1 rounded-lg">
                              <BlockIcon style={{ fontSize: "14px" }} />
                              <span className="text-xs font-medium">Restricted</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end space-x-2">
                            <Tooltip title="Edit store access">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setModalData(member);
                                  setSpecialAccessTouched(Boolean(member?.permission_type_overrides?.length));
                                  setModalEdit(true);
                                  handleOpen();
                                }}
                                className="hover:bg-gray-100"
                              >
                                <EditIcon style={{ fontSize: "16px" }} className="text-gray-600" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={`Remove ${props.selectedTeamMemberData?.first_name} from ${member?.store?.store_name}`}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedStmId(member?.id);
                                  setSelectedStoreId(member?.store?.id);
                                  setSelectedStoreName(member?.store?.store_name);
                                  handleOpenEmployeeDeleteWarningModal();
                                }}
                                className="hover:bg-red-50"
                              >
                                <DeleteIcon style={{ fontSize: "16px" }} className="text-red-600" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </EmployeeProfileSectionCard>
        ) : (
          <EmployeeProfileSectionCard bodyClassName="">
            <EmployeeProfileEmptyState
              icon={SecurityIcon}
              title="No store access"
              description='Grant this employee access to stores and set their permissions using the "Add Store Access" button above.'
            />
          </EmployeeProfileSectionCard>
        )}
      </div>

      {/* Drawer Modal - keeping your existing implementation */}
      <Drawer open={open} anchor="right" onClose={handleClose}>
        <div className="w-[450px] h-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title={modalEdit ? "Edit Store Access" : "Add Store Access"}
              onClose={handleClose}
            />
            <div className="w-full px-6">
              <div className="mt-5">
                <p className="text-sm text-slate-500">Store*</p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    displayEmpty
                    value={modalData?.store?.id || ""}
                    onChange={handleStoreChange}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      paddingY: "1px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                      height: "45px",
                      backgroundColor: "white",
                    }}
                  >
                    <MenuItem disabled value="">
                      Select store
                    </MenuItem>
                    {storeOptions?.map((store, index) => (
                      <MenuItem key={index} value={store.id}>
                        {store.store_name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="mt-7">
                <p className="text-sm text-slate-500">Permission Type*</p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    displayEmpty
                    value={modalData?.permission_type || ""}
                    onChange={handlePermissionChange}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      paddingY: "1px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                      height: "45px",
                      backgroundColor: "white",
                    }}
                  >
                    <MenuItem disabled value="">
                      Select Permission
                    </MenuItem>
                    {(props.permissionRole === "company_admin" || props.permissionRole === "rightwork_internal") && <MenuItem value="company_admin">Admin</MenuItem>}
                    <MenuItem value="company_manager">Manager</MenuItem>
                    <MenuItem value="company_employee">Employee</MenuItem>
                    {props.timeEntriesEnabled && (
                      <MenuItem value="company_timeclock">Time Clock</MenuItem>
                    )}
                  </Select>
                </div>
              </div>
              {shouldShowSpecialAccessSelect && specialAccessOptions.length > 0 && (
                <div className="mt-7">
                  <p className="text-sm text-slate-500">Add Special Access</p>
                  <div className="mt-1">
                    <Select
                      fullWidth
                      multiple
                      value={specialAccessValue}
                      onChange={handlePermissionOverrideChange}
                      size="small"
                      renderValue={renderSpecialAccessValue}
                      sx={{
                        borderRadius: "8px",
                        paddingY: "1px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                        minHeight: "45px",
                        backgroundColor: "white",
                      }}
                    >
                      {specialAccessOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                        >
                          <Checkbox
                            checked={specialAccessValue.includes(option.value)}
                            sx={{
                              "&.Mui-checked": {
                                color: "#6d28d9",
                              },
                            }}
                          />
                          <ListItemText primary={option.label} />
                          {SPECIAL_ACCESS_TOOLTIPS[option.value]?.[modalData.permission_type] && (
                            <Tooltip
                              title={SPECIAL_ACCESS_TOOLTIPS[option.value][modalData.permission_type]}
                              placement="top"
                              arrow
                            >
                              <InfoOutlinedIcon
                                className="text-gray-400"
                                style={{ fontSize: "16px", marginLeft: "8px" }}
                              />
                            </Tooltip>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              )}
              {/* Apply to All Stores checkbox */}
              {((modalEdit && props.relatedTeamMembersData?.length > 1) ||
                (!modalEdit && props.relatedTeamMembersData?.length > 0)) && (
                <div
                  className="mt-7 flex items-center cursor-pointer"
                  onClick={() => setApplyToAll((prev) => !prev)}
                >
                  <Checkbox
                    checked={applyToAll}
                    sx={{
                      padding: 0,
                      marginRight: "8px",
                      "&.Mui-checked": {
                        color: "#6d28d9",
                      },
                    }}
                  />
                  <span className="text-sm text-slate-700">
                    Apply permissions to all stores
                  </span>
                </div>
              )}
            </div>
            <div className="h-[400px]"></div>
          </div>
          <div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <LoadingButton
                  variant="contained"
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  disabled={!checkIfValid()}
                  onClick={async () => {
                    const permType = modalData?.permission_type;
                    const overrides = modalData?.permission_type_overrides || [];

                    if (applyToAll) {
                      // Build list of stores to bulk update, skipping restricted ones
                      const bulkUpdates = (props.relatedTeamMembersData || [])
                        .filter((member) => {
                          const isRestricted =
                            member?.permission_type === "company_admin" &&
                            props.permissionRole !== "company_admin" &&
                            props.permissionRole !== "rightwork_internal";
                          return !isRestricted;
                        })
                        .map((member) => ({
                          stm_id: member.id,
                          store_id: member.store?.id,
                        }));

                      if (modalEdit) {
                        // Edit mode: bulk update all stores
                        props.bulk_update_store_team_member_permissions(
                          bulkUpdates,
                          permType,
                          overrides
                        );
                      } else {
                        // Add mode: await clone completion, only bulk update on success
                        const cloneSucceeded = await props.clone_or_reactivate_store_team_member(
                          props.selectedTeamMemberData?.id,
                          modalData?.store?.id,
                          permType,
                          overrides
                        );
                        if (cloneSucceeded && bulkUpdates.length > 0) {
                          props.bulk_update_store_team_member_permissions(
                            bulkUpdates,
                            permType,
                            overrides
                          );
                        }
                      }
                    } else {
                      if (modalEdit) {
                        props.post_store_team_member_only_permission_type_and_store_id(
                          modalData?.id,
                          modalData?.store?.id,
                          permType,
                          overrides
                        );
                      } else {
                        props.clone_or_reactivate_store_team_member(
                          props.selectedTeamMemberData?.id,
                          modalData?.store?.id,
                          permType,
                          overrides
                        );
                      }
                    }
                    handleClose();
                  }}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    fontSize: "16px",
                  }}
                >
                  {modalEdit ? "Save Changes" : "Add Access"}
                </LoadingButton>
              </div>
              <div className="w-full mt-3">
                <ButtonOutlined text="Cancel" onClick={handleClose} />
              </div>
            </DrawerFooterContainerLarge>
          </div>
        </div>
      </Drawer>
      <EmployeeDeleteWarningModal
        open={openEmployeeDeleteWarningModal}
        handleClose={handleCloseEmployeeDeleteWarningModal}
        firstName={props.selectedTeamMemberData?.first_name}
        lastName={props.selectedTeamMemberData?.last_name}
        homeStore={props.homeStore}
        selectedStoreId={selectedStoreId}
        selectedStoreName={selectedStoreName}
        storeNames={props.storeNames}
        stm_id={props.selectedTeamMemberData?.id}
        selectedStmId={selectedStmId}
        archive_store_team_member={props.archive_store_team_member}
        setShowUserProfile={props.setShowUserProfile}
      />
    </>
  );
}

export default EmployeeManageAccessTab;
